import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Search, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddressAutocomplete({ value, onSelect, testId = "address-input", country = "us" }) {
  const [input, setInput] = useState(value || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
    // setOpen from useState is stable; wrapperRef is a ref. Nothing else to track.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear any previous selection when the country switches — lat/lon/label
  // become stale for the new country.
  useEffect(() => {
    setInput("");
    setSelected(null);
    setResults([]);
  }, [country]);

  const isZip = (s) => /^[A-Za-z0-9][A-Za-z0-9\s-]{2,9}$/.test(s.trim()) && /\d/.test(s);

  const runSearch = async (q) => {
    if (!q || q.length < 3) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      if (isZip(q) && /^\d/.test(q.trim())) {
        // Numeric-first looks like a zip/postal code in many countries.
        const r = await axios.get(`${API}/geocode/zip`, { params: { zip: q.trim(), country } });
        setResults([
          {
            display_name: r.data.display_name,
            lat: r.data.lat,
            lon: r.data.lon,
            zip_code: q.trim(),
          },
        ]);
      } else {
        const r = await axios.get(`${API}/geocode/search`, { params: { q, country } });
        setResults(r.data.results || []);
      }
      setOpen(true);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setInput(v);
    setSelected(null);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(v), 350);
  };

  const handlePick = (r) => {
    setSelected(r);
    setInput(r.display_name);
    setOpen(false);
    onSelect({
      label: r.display_name,
      lat: r.lat,
      lon: r.lon,
      zip_code: r.zip_code || null,
    });
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex items-center border border-black bg-white">
        <div className="px-3 py-3 border-r border-black">
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : selected ? (
            <Check className="w-4 h-4" data-testid="address-validated-icon" />
          ) : (
            <Search className="w-4 h-4" />
          )}
        </div>
        <input
          data-testid={testId}
          className="flex-1 px-3 py-3 outline-none font-normal text-[15px] bg-white"
          placeholder="Street address or ZIP"
          value={input}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          autoComplete="off"
        />
      </div>
      {open && results.length > 0 && (
        <ul
          data-testid="address-suggestions"
          className="absolute z-20 top-full left-0 right-0 bg-white border border-black border-t-0 max-h-64 overflow-auto shadow-sm"
        >
          {results.map((r, i) => (
            <li
              key={`${r.lat}-${r.lon}-${i}`}
              data-testid={`address-suggestion-${i}`}
              className="px-4 py-3 text-[13px] hover:bg-[#FFD600] cursor-pointer border-b border-neutral-200 last:border-b-0 transition-colors"
              onClick={() => handlePick(r)}
            >
              {r.display_name}
            </li>
          ))}
        </ul>
      )}
      {selected && (
        <div className="mt-2 ticker-label" data-testid="address-selected-confirm">
          Validated · {selected.lat.toFixed(3)}, {selected.lon.toFixed(3)}
        </div>
      )}
    </div>
  );
}
